import React, { useEffect, useState } from 'react';
import { Upload, Button, Input, Space, message, Modal, Table, Row, Col } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import AuditModal from './AuditModal';

interface UploadSectionProps {
  onReportUpload: (file: File) => void;
  onApplicationUpload: (file: File) => void;
  onProcessAppForm: () => void;
  onStartAudit: (noApplication?: boolean) => void;
  application: Record<string, any>;
  applicating: boolean;
}

interface SimilarReport {
  id: number;
  reportNumber: string;
  auditUnit: string;
  auditStatus: string;
}
interface ModalData {
  visible?: boolean,
  confirmEvt: () => void,
  closeEvt: () => void
  noApplication?: boolean
}

const UploadSection: React.FC<UploadSectionProps> = ({ onReportUpload, onApplicationUpload, onProcessAppForm, onStartAudit, application, applicating }) => {
  const [uploadedReport, setUploadedReport] = useState<File | null>(null);
  const [searchText, setSearchText] = useState('');
  const [similarModalVisible, setSimilarModalVisible] = useState(false);
  const [similarReports, setSimilarReports] = useState<SimilarReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<SimilarReport | null>(null);
  const [reportBtnDisabled, setReportBtnDisabled] = useState(true);
  const [applicationBtnDisabled, setApplicationBtnDisabled] = useState(true);
  const [modalData, setModalData] = useState<ModalData>({visible: false, confirmEvt: () => {}, closeEvt: () => {}});
  useEffect(() => {
  }, [])

  const handleUpload = (fileType:string, info: any) => {
    if (info.file.status === 'removed') return;

    // Extract the file from fileList[0] since originFileObj might be undefined
    const fileListItem = info.fileList[0];
    if (!fileListItem) {
      message.error('文件上传失败，请重试！');
      console.error('No file in fileList:', info);
      return;
    }

    // Try to get the File object from fileList[0]
    let file: File;
    if (fileListItem.originFileObj) {
      file = fileListItem.originFileObj as File;
    } else if (fileListItem && 'file' in fileListItem) {
      // Fallback: Try to access file directly if originFileObj is not available
      file = fileListItem.file as File;
    } else {
      message.error('无法获取文件对象，请检查文件上传！');
      console.error('File object not found in fileList:', fileListItem);
      return;
    }

    console.log('Selected file:', file, 'File name:', file.name, 'File type:', file.type); // Debug log

    // Check if it’s a PDF file
    const suffix = (file.name.split('.').pop() || '').toLowerCase();
    const isAllow = file.type === 'application/pdf' || ['pdf', 'doc', 'docx', '.dotx'].includes(suffix);
    if (!isAllow && fileType === 'report') {
      message.error('请上传 PDF 或 Word 报告文件！');
      return;
    }

    if (fileType === 'report') {
      setUploadedReport(file); // set current report
      onReportUpload(file); // Pass the File object
      message.success(`报告文件 ${file.name} 上传成功！`);
      setApplicationBtnDisabled(true);
      setReportBtnDisabled(false);
    } else {
      onApplicationUpload(file);
      message.success(`申请表文件 ${file.name} 上传成功！`);
      setReportBtnDisabled(true);
      setApplicationBtnDisabled(false);
    }
  };

  const handleReportUpload = (info: any) => {
    return handleUpload('report', info);
  }

  const handleAppFormUpload = (info: any) => {
    return handleUpload('applicationForm', info);
  }

  const handleSimilarCompare = async () => {
    try {
      const response = await api.get('/api/similar-reports/');
      setSimilarReports(response.data);
      setSimilarModalVisible(true);
    } catch (error) {
      message.error('获取相似报告失败');
      console.error('Failed to fetch similar reports:', error);
    }
  };

  const columns = [
    {
      title: '报告编号',
      dataIndex: 'reportNumber',
      key: 'reportNumber',
    },
    {
      title: '报告抬头公司',
      dataIndex: 'auditUnit',
      key: 'auditUnit',
    },
    {
      title: '审核状态',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: SimilarReport) => (
        <Button
          type="link"
          onClick={() => setSelectedReport(record)}
        >
          选择
        </Button>
      ),
    },
  ];
  const startAudit = () => {
    setModalData({
      visible: true,
      confirmEvt: (noApplication?: boolean) => {
        onStartAudit(noApplication);
      },
      closeEvt() {
        setModalData({ confirmEvt: () => {}, closeEvt: () => {}, visible: false });
      },
    })
  }

  return (
    <Space direction="horizontal" size="middle" style={{ marginBottom: '10px' }}>
      <Upload
        beforeUpload={() => false} // Prevent automatic upload, handle manually
        onChange={handleAppFormUpload}
        fileList={[]} // Do not show file list
        accept=".pdf,.doc,.docx,.jpg,.png" // Allow multiple file types
        showUploadList={false} // Hide upload list
        name='applicatonForm'
      >
        <Button icon={<UploadOutlined />}>上传申请表</Button>
      </Upload>
      <Upload
        beforeUpload={() => false} // Prevent automatic upload, handle manually
        onChange={handleReportUpload}
        fileList={[]} // Do not show file list
        accept=".pdf,.doc,.docx" // Restrict to PDF files only
        showUploadList={false} // Hide upload list
        name='report'
        disabled={applicating} // 解析申请表期间，不允许上传报告
      >
        <Button icon={<UploadOutlined />}>上传报告</Button>
      </Upload>
      
      <Input
        placeholder="其他信息"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ width: 300 }}
      />
      <Button type="primary" id='appform' onClick={() => {
        onProcessAppForm()
        setApplicationBtnDisabled(true)
      }} disabled={applicationBtnDisabled}>解析申请表</Button>
      <Button type="primary" id='reviewReport' onClick={() => {
        startAudit()
        setReportBtnDisabled(true)
      }} disabled={reportBtnDisabled}>开始AI审核</Button>
      <Button type="default" id='relatedReports' onClick={handleSimilarCompare}>相似报告对比</Button>

      <Modal
        title="相似报告对比"
        visible={similarModalVisible}
        onCancel={() => setSimilarModalVisible(false)}
        footer={null}
        width={1000}
      >
        <div style={{ height: '500px' }}>
          {/* 上半部分：相似报告列表 */}
          <div style={{ height: '50%', overflowY: 'auto', marginBottom: '10px' }}>
            <Table
              columns={columns}
              dataSource={similarReports}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </div>

          {/* 下半部分：报告预览 */}
          {selectedReport && (
            <div style={{ height: '50%', overflowY: 'auto' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <h3>当前审核报告</h3>
                  <div style={{ border: '1px solid #d9d9d9', padding: '10px', height: '200px', overflowY: 'auto' }}>
                    {uploadedReport ? (
                      <iframe
                        src={URL.createObjectURL(uploadedReport)}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Current Report"
                      />
                    ) : (
                      <p>请上传报告以预览</p>
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <h3>相似报告</h3>
                  <div style={{ border: '1px solid #d9d9d9', padding: '10px', height: '200px', overflowY: 'auto' }}>
                    {selectedReport ? (
                      <iframe
                        src={`/api/similar-reports/${selectedReport.id}/preview/`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Similar Report"
                      />
                    ) : (
                      <p>请选择相似报告</p>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Modal>
      {
        modalData.visible && <AuditModal {...modalData} applicationName={application?.Report_Title__c} noApplication={!application || JSON.stringify(application) === '{}'}/>
      }
    </Space>
  );
};

export default UploadSection;